import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { SortableTable } from './InsightsDashboard';
import InternalPage from './InternalPage';

// Single-page dashboard for the "electrician" role: fleet health + emergency stop
// widgets pulled from InsightsDashboard's killswitch/overview tabs, and the Internal
// Dashboard, all on one page with a shared set of filters.
const ElectricianDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState(30);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [isTodaySelected, setIsTodaySelected] = useState(false);

  const [hospitals, setHospitals] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [availableRobots, setAvailableRobots] = useState([]);
  const [selectedRobot, setSelectedRobot] = useState(null);

  const [robots, setRobots] = useState({ robots: [] });
  const [killswitchKPIs, setKillswitchKPIs] = useState({ top_operators: [], top_robots: [], by_hospital: [] });

  const [showFleetModal, setShowFleetModal] = useState(false);
  const [fleetSessions, setFleetSessions] = useState([]);
  const [selectedFleetRobot, setSelectedFleetRobot] = useState(null);

  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyStopSessions, setEmergencyStopSessions] = useState([]);
  const [selectedEmergencyHospital, setSelectedEmergencyHospital] = useState(null);

  // Electrician is full-access, so the hospital list is loaded once on mount.
  useEffect(() => {
    analyticsAPI.getHospitals()
      .then(res => setHospitals(res.data?.hospitals || []))
      .catch(() => setHospitals([]));
  }, []);

  useEffect(() => {
    const loadRobots = async () => {
      try {
        const startDate = customDateRange.start || null;
        const endDate = customDateRange.end || null;
        const res = await analyticsAPI.getRobots(selectedHospital, timeRange, null, startDate, endDate);
        setAvailableRobots(res.data?.robots || []);
        setSelectedRobot(null);
      } catch (error) {
        console.error('Failed to load robots:', error);
        setAvailableRobots([]);
      }
    };
    loadRobots();
  }, [selectedHospital, timeRange, customDateRange]);

  const loadFleetAnalytics = async () => {
    try {
      const startDate = customDateRange.start || null;
      const endDate = customDateRange.end || null;
      const res = await analyticsAPI.getPerformanceKPIs(selectedHospital, timeRange, selectedRobot, startDate, endDate);
      setRobots({ robots: res.data?.top_performers?.robots || [] });
    } catch (error) {
      console.error('Failed to load fleet analytics:', error);
      setRobots({ robots: [] });
    }
  };

  const loadKillswitchKPIs = async () => {
    try {
      let days = timeRange;
      if (customDateRange.start && customDateRange.end) {
        const start = new Date(customDateRange.start);
        const end = new Date(customDateRange.end);
        days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      }
      const startDate = customDateRange.start || null;
      const endDate = customDateRange.end || null;
      const res = await analyticsAPI.getKillswitchKPIs(days, selectedHospital, selectedRobot, startDate, endDate);
      if (res.data) setKillswitchKPIs(res.data);
    } catch (error) {
      console.error('Failed to load killswitch KPIs:', error);
    }
  };

  useEffect(() => {
    loadFleetAnalytics();
    loadKillswitchKPIs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, selectedHospital, selectedRobot, customDateRange]);

  const loadFleetSessions = async (robot) => {
    try {
      const startDate = customDateRange.start || null;
      const endDate = customDateRange.end || null;
      const res = await analyticsAPI.getFleetSessions(
        selectedHospital, robot.serial, null, null, null, timeRange, startDate, endDate
      );
      setFleetSessions(res.data.sessions || []);
      setSelectedFleetRobot(robot);
      setShowFleetModal(true);
    } catch (error) {
      console.error('Failed to load fleet sessions:', error);
      alert('Failed to load detailed sessions. Please try again.');
    }
  };

  const loadEmergencyStopSessions = async (hospital) => {
    try {
      const startDate = customDateRange.start || null;
      const endDate = customDateRange.end || null;
      const res = await analyticsAPI.getSessions(
        hospital.hospital_id, null, startDate, endDate, 1000, 1, 'Emergency Stop Activated'
      );
      const transformedSessions = (res.data.sessions || []).map(session => ({
        MachineStartTime: session.start_time,
        CleaningCycleEndTime: session.end_time,
        Serial: session.robot_serial,
        Operator: session.operator_name,
        WardName: session.ward_name,
        BedName: session.bed_name,
        TimeMinutes: session.duration_minutes ? `${session.duration_minutes} min` : '0 min',
        Code: session.error_code || 'Emergency Stop Activated',
        Hospital_Name: session.hospital_name,
      }));
      setEmergencyStopSessions(transformedSessions);
      setSelectedEmergencyHospital(hospital);
      setShowEmergencyModal(true);
    } catch (error) {
      console.error('Failed to load emergency stop sessions:', error);
      alert('Failed to load emergency stop sessions. Please try again.');
    }
  };

  const exportToCSV = (data, filename, columns) => {
    if (!data || !Array.isArray(data) || data.length === 0) return;
    const keys = columns ? columns.map(c => c.key) : Object.keys(data[0]);
    const headers = columns ? columns.map(c => c.label) : keys;
    const csvContent = [
      headers.map(h => JSON.stringify(h ?? '')).join(','),
      ...data.map(row => keys.map(k => JSON.stringify(row[k] ?? '')).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleRefresh = () => {
    loadFleetAnalytics();
    loadKillswitchKPIs();
  };

  // PWA: manifest + service worker are scoped to this route only. They're injected on
  // mount and torn down on unmount so no other role ever sees a manifest link or has a
  // service worker registered against their session.
  useEffect(() => {
    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    manifestLink.href = '/electrician-manifest.json';
    document.head.appendChild(manifestLink);

    const appleCapable = document.createElement('meta');
    appleCapable.name = 'apple-mobile-web-app-capable';
    appleCapable.content = 'yes';
    document.head.appendChild(appleCapable);

    const appleIcon = document.createElement('link');
    appleIcon.rel = 'apple-touch-icon';
    appleIcon.href = '/fybatex-logo.png';
    document.head.appendChild(appleIcon);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/electrician-sw.js', { scope: '/electrician-dashboard' })
        .catch(err => console.error('Electrician service worker registration failed:', err));
    }

    return () => {
      document.head.removeChild(manifestLink);
      document.head.removeChild(appleCapable);
      document.head.removeChild(appleIcon);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Tabs + Filters */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Tabs — at the top of the page */}
          <div className="flex gap-2 pt-4 md:pt-5 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                activeTab === 'overview' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Fleet Overview
            </button>
            <button
              onClick={() => setActiveTab('internal')}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                activeTab === 'internal' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Internal Dashboard
            </button>
          </div>

          {activeTab === 'overview' ? (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 py-4 md:py-5">
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-800">Electrician Fleet Overview</h1>
              <p className="text-slate-400 text-sm">Robot fleet health and emergency stop activity across all hospitals</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                value={selectedHospital || ''}
                onChange={(e) => setSelectedHospital(e.target.value ? parseInt(e.target.value) : null)}
                className="flex-1 sm:flex-none bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px] sm:min-w-[180px] text-sm font-medium"
              >
                <option value="">All Hospitals</option>
                {hospitals.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>

              <select
                value={selectedRobot || ''}
                onChange={(e) => setSelectedRobot(e.target.value || null)}
                className="flex-1 sm:flex-none bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[120px] sm:min-w-[140px] text-sm font-medium"
              >
                <option value="">All Robots</option>
                {availableRobots.map((r, idx) => (
                  <option key={`filter-robot-${idx}-${r.serial}`} value={r.serial}>{r.serial}</option>
                ))}
              </select>

              <select
                value={showCustomDatePicker ? 'custom' : (isTodaySelected ? 'today' : (timeRange === 36500 ? 'all' : timeRange))}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setShowCustomDatePicker(true);
                    setIsTodaySelected(false);
                    setTimeRange(null);
                  } else if (e.target.value === 'today') {
                    setShowCustomDatePicker(false);
                    setIsTodaySelected(true);
                    setTimeRange(1);
                    setCustomDateRange({ start: '', end: '' });
                  } else if (e.target.value === 'all') {
                    setShowCustomDatePicker(false);
                    setIsTodaySelected(false);
                    setTimeRange(36500);
                    setCustomDateRange({ start: '', end: '' });
                  } else {
                    setShowCustomDatePicker(false);
                    setIsTodaySelected(false);
                    setTimeRange(parseInt(e.target.value));
                    setCustomDateRange({ start: '', end: '' });
                  }
                }}
                className="flex-1 sm:flex-none bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[130px] text-sm font-medium"
              >
                <option value="today">Today</option>
                <option value={7}>Last 7 Days</option>
                <option value={14}>Last 14 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={60}>Last 60 Days</option>
                <option value={90}>Last 90 Days</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </select>

              {showCustomDatePicker && (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="flex-1 sm:flex-none min-w-[130px] bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="flex-1 sm:flex-none min-w-[130px] bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              )}

              <button
                onClick={handleRefresh}
                className="flex-1 sm:flex-none justify-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors text-sm flex items-center gap-2 shadow-sm shadow-blue-500/30"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          ) : (
          <div className="py-4 md:py-5">
            <h1 className="text-xl font-bold tracking-tight text-slate-800">Internal Dashboard</h1>
            <p className="text-slate-400 text-sm">Test/maintenance session data excluded from production analytics</p>
          </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6 md:space-y-8">
        {activeTab === 'overview' && (
        <>
        {/* Robot Fleet */}
        <SortableTable
          title="Robot Fleet Analysis"
          data={robots.robots}
          defaultSortKey="total_sessions"
          onExport={() => exportToCSV(robots.robots, 'robot_fleet_analysis')}
          onRowClick={loadFleetSessions}
          columns={[
            { key: 'serial', label: 'Robot ID' },
            { key: 'hospital', label: 'Hospital' },
            { key: 'total_sessions', label: 'Sessions' },
            { key: 'success_rate', label: 'Success Rate', render: (val) => (
              <span className={`font-medium ${val >= 80 ? 'text-emerald-500' : val >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                {val}%
              </span>
            )},
            { key: 'avg_duration', label: 'Avg Duration', render: (val) => `${val} min` },
            { key: 'total_hours', label: 'Total Hours', render: (val) => `${val}h` },
            { key: 'status', label: 'Status', render: (val, row) => (
              row.is_high_error ? (
                <span className="px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium">High Errors</span>
              ) : row.is_outlier_high ? (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-600 rounded-full text-xs font-medium">Top Performer</span>
              ) : row.is_outlier_low ? (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-600 rounded-full text-xs font-medium">Low Usage</span>
              ) : (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">Normal</span>
              )
            )},
          ]}
        />

        {/* Emergency Stops by Hospital */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-3 p-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800">Emergency Stops by Hospital</h3>
              <p className="text-xs text-slate-400">Click any hospital to view detailed emergency stop sessions</p>
            </div>
          </div>
          {killswitchKPIs.by_hospital && killswitchKPIs.by_hospital.length > 0 ? (
            <SortableTable
              data={killswitchKPIs.by_hospital}
              defaultSortKey="stop_count"
              onRowClick={loadEmergencyStopSessions}
              onExport={() => exportToCSV(killswitchKPIs.by_hospital, 'emergency_stops_by_hospital')}
              columns={[
                { key: 'hospital_name', label: 'Hospital' },
                { key: 'stop_count', label: 'Emergency Stops', render: (val) => (
                  <span className="font-semibold text-red-600">{val}</span>
                )},
                { key: 'affected_robots', label: 'Affected Robots', render: (val) => (
                  <span className="text-slate-700">{val}</span>
                )},
                { key: 'affected_operators', label: 'Operators Involved', render: (val) => (
                  <span className="text-slate-700">{val}</span>
                )},
                { key: 'locations_count', label: 'Unique Locations', render: (val) => (
                  <span className="text-slate-600">{val}</span>
                )},
              ]}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-700">No data available</p>
              <p className="text-xs text-slate-400 mt-1">No emergency stops recorded in this period</p>
            </div>
          )}
        </div>

        {/* Most Affected Robots + Top Emergency Stop Operators — paired side by side from tablet width up */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-3 p-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800">Most Affected Robots</h3>
              <p className="text-xs text-slate-400">Robots with the most emergency stop events in this period</p>
            </div>
          </div>
          {killswitchKPIs.top_robots && killswitchKPIs.top_robots.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <th className="text-left px-5 py-3 font-medium">Robot Serial</th>
                  <th className="text-right px-5 py-3 font-medium">Stops</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {killswitchKPIs.top_robots.slice(0, 10).map((robot, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-800 font-mono text-xs">{robot.robot_serial}</td>
                    <td className="px-5 py-3 text-right">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">{robot.stop_count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-700">No data</p>
              <p className="text-xs text-slate-400 mt-1">No emergency stops recorded in this period</p>
            </div>
          )}
        </div>

        {/* Top Emergency Stop Operators */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-3 p-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5-4a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800">Top Emergency Stop Operators</h3>
              <p className="text-xs text-slate-400">Operators most frequently involved in emergency stop events</p>
            </div>
          </div>
          {killswitchKPIs.top_operators && killswitchKPIs.top_operators.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <th className="text-left px-5 py-3 font-medium">Operator</th>
                  <th className="text-right px-5 py-3 font-medium">Stops</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {killswitchKPIs.top_operators.slice(0, 10).map((op, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-800">{op.operator_name || `Operator ${op.operator_id}`}</td>
                    <td className="px-5 py-3 text-right">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{op.stop_count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-700">No data</p>
              <p className="text-xs text-slate-400 mt-1">No emergency stops recorded in this period</p>
            </div>
          )}
        </div>
        </div>
        </>
        )}

        {activeTab === 'internal' && (
          <InternalPage user={user} showGonxtBadge={false} fullPage={false} />
        )}
      </div>

      {/* Fleet Sessions Modal */}
      {showFleetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Fleet Session Details</h2>
                {selectedFleetRobot && (
                  <p className="text-sm text-blue-100 mt-1">
                    Robot: {selectedFleetRobot.serial} | Hospital: {selectedFleetRobot.hospital} | Sessions: {selectedFleetRobot.total_sessions}
                  </p>
                )}
              </div>
              <button onClick={() => setShowFleetModal(false)} className="text-white hover:text-blue-100 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {fleetSessions.length > 0 ? (
                <SortableTable
                  data={fleetSessions}
                  defaultSortKey="MachineStartTime"
                  defaultSortDir="desc"
                  onExport={(rows, cols) => exportToCSV(rows, `fleet_sessions_${selectedFleetRobot?.serial || 'export'}`, cols)}
                  columns={[
                    { key: 'Hospital_Name', label: 'Hospital' },
                    { key: 'Serial', label: 'Robot' },
                    { key: 'MachineStartTime', label: 'Session Start', render: (val) => val || '-' },
                    { key: 'CleaningCycleEndTime', label: 'Cycle End', render: (val) => val || '-' },
                    { key: 'TimeMinutes', label: 'Duration', render: (val) => <span>{val}</span> },
                    { key: 'Code', label: 'Result' },
                    { key: 'WardName', label: 'Ward', render: (val) => val || '-' },
                    { key: 'BedName', label: 'Bed', render: (val) => val || '-' },
                    { key: 'Operator', label: 'Operator', render: (val) => val || '-' },
                  ]}
                />
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-2">📊</div>
                  <p className="font-medium">No session data available</p>
                  <p className="text-sm">Try adjusting your date range</p>
                </div>
              )}
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <button onClick={() => setShowFleetModal(false)} className="px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Stop Sessions Modal */}
      {showEmergencyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-red-600 to-red-800 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Emergency Stop Sessions Report</h2>
                {selectedEmergencyHospital && (
                  <p className="text-sm text-red-100 mt-1">
                    Hospital: {selectedEmergencyHospital.hospital_name} | Total Emergency Stops: {selectedEmergencyHospital.stop_count}
                  </p>
                )}
              </div>
              <button onClick={() => setShowEmergencyModal(false)} className="text-white hover:text-red-100 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {emergencyStopSessions.length > 0 ? (
                <SortableTable
                  data={emergencyStopSessions}
                  defaultSortKey="MachineStartTime"
                  defaultSortDir="desc"
                  onExport={() => exportToCSV(emergencyStopSessions, `emergency_stops_${selectedEmergencyHospital?.hospital_name || 'export'}`)}
                  columns={[
                    { key: 'MachineStartTime', label: 'Timestamp', render: (val) => new Date(val).toLocaleString() },
                    { key: 'Serial', label: 'Robot', render: (val) => (
                      <span className="font-mono text-xs font-medium text-slate-800">{val}</span>
                    )},
                    { key: 'Operator', label: 'Operator', render: (val) => val || 'Unknown' },
                    { key: 'WardName', label: 'Ward', render: (val) => val || '-' },
                    { key: 'BedName', label: 'Location', render: (val) => val || '-' },
                    { key: 'TimeMinutes', label: 'Duration', render: (val) => val || '0 min' },
                    { key: 'Code', label: 'Status', render: (val) => (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        {val}
                      </span>
                    )},
                  ]}
                />
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-2">🚨</div>
                  <p className="font-medium">No emergency stop sessions found</p>
                  <p className="text-sm">This hospital has no recorded emergency stops in the selected period</p>
                </div>
              )}
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <button onClick={() => setShowEmergencyModal(false)} className="px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectricianDashboard;
